import { Box, Divider, Flex, Skeleton, Text } from '@chakra-ui/react';
import ProgressBar from './ProgressBar';
import { FlagIcon, TrophyIcon } from '../../icons';
import {
  getPercentage,
  LegendItemProps,
  progressBarColors,
} from '../../shared/utils';
import { KeyResultStatusData } from '../types';

type Props = {
  data: KeyResultStatusData;
  isGoal?: boolean;
  isLoading?: boolean;
  origin?: string;
  team?: string;
  title: string;
};

const LegendItem = ({
  label,
  value,
  total,
  color,
  hasBorder,
}: LegendItemProps) => (
  <Flex
    align={'center'}
    gap={1}
    borderRight={hasBorder ? '1px solid' : undefined}
    borderColor={'text.tertiary'}
    paddingRight={hasBorder ? 2 : undefined}
  >
    <Box bg={color} h={'8px'} w={'8px'} rounded={'2px'} />
    <Text fontSize={'xs'} color={'text.secondary2'}>
      {label}{' '}
      <Text as={'span'} fontSize={'sm'} color={'text.gray.300'}>
        {getPercentage(value, total)}%
      </Text>
    </Text>
  </Flex>
);

export const GoalDetails = ({
  data,
  isLoading,
  isGoal,
  origin,
  team,
  title,
}: Props) => {
  const items = [
    {
      label: 'At Risk',
      value: data?.atRiskCount,
      color: progressBarColors.atRisk,
      hasBorder: true,
    },
    {
      label: 'Behind',
      value: data?.behindCount,
      color: progressBarColors.behind,
      hasBorder: true,
    },
    {
      label: 'On Track',
      value: data?.onTrackCount,
      color: progressBarColors.onTrack,
      hasBorder: false,
    },
  ];

  const filteredItems = items.filter(
    (item) =>
      item.value !== 0 &&
      item.value !== null &&
      item.value !== undefined &&
      !isNaN(item.value)
  );

  if (filteredItems.length > 0) {
    filteredItems[filteredItems.length - 1].hasBorder = false;
  }

  const total = filteredItems?.reduce((sum, item) => sum + item.value, 0);

  return (
    <Flex
      direction="column"
      gap={3}
      minH={origin === 'team-table' ? '123px' : '100%'}
      w={'full'}
    >
      <Flex align="center" justify="space-between">
        {isLoading ? (
          <Skeleton height="18px" width="45%" />
        ) : (
          <>
            <Flex
              align={'center'}
              gap={'8px'}
              fontFamily="heading"
              fontSize="sm"
              color="text.gray.300"
              mt={1}
            >
              {isGoal ? <FlagIcon /> : <TrophyIcon />}
              <Text fontWeight={'normal'}>
                {title}:{' '}
                <Text as={'span'} fontWeight={'medium'}>
                  {data?.totalCount}
                </Text>
              </Text>
            </Flex>
          </>
        )}
      </Flex>

      {isLoading ? (
        <Skeleton height="32px" width="100%" />
      ) : (
        <ProgressBar
          data={[
            {
              atRisk: data?.atRiskCount || 0,
              behind: data?.behindCount || 0,
              onTrack: data?.onTrackCount || 0,
            },
          ]}
        />
      )}

      <Flex align={'center'} gap={2}>
        {filteredItems.length > 0 ? (
          filteredItems.map((item, index) => (
            <LegendItem
              key={index}
              label={item.label}
              value={item.value}
              total={total}
              color={item.color}
              hasBorder={item.hasBorder}
            />
          ))
        ) : (
          <Text fontSize="xs" color="text.secondary2">
            No data available
          </Text>
        )}
      </Flex>
    </Flex>
  );
};

export default GoalDetails;
